import { TogetherTypes } from '@hhgtech/hhg-components'
import { DataTrackingTypeHTMLElement } from '@hhgtech/hhg-components/types'

import { ScheduleEventStatus } from 'components/organisms/serviceConnections/scheduleEvent/context'
import type { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { PhoneVerifyType } from 'state/types'

import { Article, ArticleApi } from './Article'
import { Category, CategoryApi } from './Category'
import { Product } from './Ecommerce'
import { HealthtoolReviewer } from './HealthTool'
import { CareDoctor, CareService, CareServiceInfoApi } from './QuickBooking'
import { ReviewedUserApi } from './Reviewed'
import { ListBanner } from './Spotlight'

export enum SessionType {
  HERO_BANNER_DEFAULT = 'hero_banner_default',
  HERO_BANNER_FULLWIDTH = 'hero_banner_full_width',
  HERO_BANNER_APP = 'header_section',
  EMBEDDING_DOCTORS = 'embedding_doctors',
  CLINIC_LOCATORS = 'clinic_locators',
  EMBEDDING_RISKSCREENER = 'embedding_riskscreener',
  EMBEDDING_RISKSCREENER_MULTI = 'embedding_riskscreenings_multi',
  VIDEOS = 'videos',
  VIDEO_SECTION = 'video_section',
  ARTICLES = 'articles',
  CLICKABLE_BANNER = 'clickable_banners',
  ECOMMERCE_PRODUCS = 'pmc_products',
  COMMUNITY_WITH_POSTS = 'community_with_posts',
  CHAT_WITH_EXPERT = 'chat_with_expert',
  LEADGEN = 'leadgen_form',
  SOYA_EXTERNAL_LINK = 'soya_external_link',
  SOYA_OPEN_CHAT = 'soya_open_chat',
  HEALTH_TOOL = 'embed_health_tool',
  HEALTH_TOOL_LIST = 'health_tools_section',
  CONTENT_BANNER = 'content_banner_section',
  FOOTER_BANNER = 'footer_banner_section',
  VISION_SECTION = 'vision_section',
  TOP = 'TOP',
  QUOTATION = 'quotation',
  STATIC_BANNER = 'static_banner',
  SCHEDULED_EVENT = 'scheduled_event',
  CONTENT_EXPERT = 'content-expert',
  CUSTOMIZED_NAVIGATOR = 'customized_navigation',
  CUSTOMIZED_BLOCK = 'customize_block',
  CTA_BANNER = 'cta_banners',
}

export interface SpotlightSessionCommonTypeApi {
  session_layout: SessionType
  title: string
  description: string
  id_scroll: string
  hashtag: string
  background_color: string
  icon?: string
}

export interface SpotlightSessionCommonType {
  sessionLayout: SessionType
  title: string
  description: string
  icon: string
  idScroll: string
  backgroundColor: string
  hashtag?: string
  sessionId?: string
  wrapperProps: {
    title: string
    description: string
    icon: string
    bgColor: string
  }
}

export type SpotlightLeadgenSessionApi = {
  campaign_code: string
  banner: {
    desktop: string
    mobile: string
  }
  term_and_condition: string
  incentive_message: {
    title: string
    description: string
  }
  thank_you_message: {
    title: string
    description: string
  }
  phone_verification: PhoneVerifyType
  cta_button_title: string
  is_skippable: boolean
  popup_appearance: boolean
  campaigns: any
} & SpotlightSessionCommonTypeApi

export type SpotlightLeadgenSession = {
  campaignCode: string
  banner: {
    desktop: string
    mobile: string
  }
  termAndCondition: string
  incentiveMessage: {
    title: string
    description: string
  }
  thankYouMessage: {
    title: string
    description: string
    image?: string
  }
  phoneVerification: PhoneVerifyType
  ctaButtonTitle: string
  isSkippable: boolean
  isModal: boolean
  campaigns: any
} & SpotlightSessionCommonType

export enum FeedbackStatus {
  // #1: Corresponding with "Inaccurate"
  Inaccurate = 1,
  // #2: Corresponding with "Doesn’t have the information I’m looking for"
  NoRelated,
  // #3: Corresponding with "Other" + message
  Other,
}

export type SpotlightHealthToolSessionApi = {
  health_tool: {
    id: string
    title: string
    icon: string
    link: string
    subtitle: string
    template: HEALTH_TOOL_NAME
    is_subot: boolean
    disclaimer: string
    reviewed_by: ReviewedUserApi
  }
  reviewed_date: string
} & SpotlightSessionCommonTypeApi

export type SpotlightHealthToolSession = {
  healthTool: {
    id: string
    title: string
    icon: string
    link: string
    subTitle: string
    template: HEALTH_TOOL_NAME
    isSubot: boolean
    disclaimer: string
    reviewedBy: HealthtoolReviewer
  }
} & SpotlightSessionCommonType

export type SpotlightRiskScreenerSessionApi = {
  select_subot: string | string[]
} & SpotlightSessionCommonTypeApi

export type SpotlightRiskScreenerSession = {
  selectSubot: string[]
} & SpotlightSessionCommonType

export type SpotlightMultiRiskScreenerSessionApi = {
  list_subot: {
    subot: string
    background_color: string
    button_color: string
  }[]
  horizontal_display: boolean
} & SpotlightSessionCommonTypeApi

export type SpotlightMultiRiskScreenerSession = {
  listSubot: {
    id: string
    backgroundColor: string
    buttonColor: string
  }[]
  horizontalDisplay: boolean
} & SpotlightSessionCommonType

export type SpotlightMedicalFactItem = {
  content: string
  link: {
    title: string
    url: string
  }
}

export type ScrollToItemTypeApi = {
  id_scroll: string
  title: string
  description: string
  cta_title: string
  session_layout: SessionType
  hashtag?: string
  icon: string
  is_banner: boolean
  url: string
  type: 'normal' | 'comming_soon'
}

export type ScrollToItemType = {
  idScroll: string
  title: string
  description: string
  ctaTitle: string
  sessionLayout: SessionType
  hashtag?: string
  image?: string
  isImageBg?: boolean
  url: string
  type?: 'normal' | 'comming_soon' | 'none' | 'open_chat'
}

export type HeroBannerApi = {
  desktop_banner: string
  mobile_banner: string
  link_to: string
  cta_title: string
  title: string
}

export type HeroBannerItem = {
  desktopBanner: string
  mobileBanner: string
  ctaTitle: string
  linkTo: string
  title: string
}

export type HeroBanner = {
  listBanner: HeroBannerItem[]
  desktopBanner: string
  mobileBanner: string
  linkTo: string
  title: string
  ctaTitle: string
  ratio?: number
  hasLeavePageConfirm?: boolean
  keepRatio?: boolean
  onBannerLoad?: () => void
  dataTracking?: DataTrackingTypeHTMLElement
  primaryColor?: string
  autoSlide?: boolean
  delayTime?: number
}

export type SpotlightHeroBannerSessionApi = {
  desktop_banner: string
  mobile_banner: string
  is_cta_banner: boolean
  cta_title: string
  link_to: string
  image_banner: HeroBannerApi[]
  list_banner: HeroBannerApi[]
  auto_slide_timing: number
  auto_slide: number
  medical_facts: {
    title: string
    list_content: SpotlightMedicalFactItem[]
  }
  navigating: ScrollToItemTypeApi[]
  dynamic_banner: boolean
} & SpotlightSessionCommonTypeApi

export type SpotlightHeroBannerAppSessionApi = {
  information: {
    title: string
  }
  banner: {
    desktop: string
    mobile: string
  }
  header_description: string
  navigating: ScrollToItemTypeApi[]
} & SpotlightSessionCommonTypeApi

export type SpotlightHeroBannerAppSession = {
  banner: {
    desktop: string
    mobile: string
  }
  headerDescription: string
  navigating: ScrollToItemType[]
} & SpotlightSessionCommonType

export type MedicalFact = {
  title: string
  listContent: SpotlightMedicalFactItem[]
}

export type SpotlightHeroBannerSession = {
  imageBanner: HeroBanner[]
  isCtaBanner: boolean
  medicalFacts: MedicalFact
  navigating: ScrollToItemType[]
  listBanner: HeroBannerItem[]
  autoSlideTiming: number
  isDynamicBanner: boolean
} & SpotlightSessionCommonType

export type SpotlightEmbeddingDoctorType =
  | 'doctors'
  | 'specialties'
  | 'top_service'

export type SpotlightEmbeddingDoctorsSessionApi = {
  type_embedding: SpotlightEmbeddingDoctorType
  embeb_ids: string[]
  specialties: string[]
  campaign: {
    name: string
    source: string
  }
} & SpotlightSessionCommonTypeApi

export type SpotlightEmbeddingDoctorsSession = {
  typeEmbedding: SpotlightEmbeddingDoctorType
  embebIds: string[]
  specialties: string[]
  doctors: CareDoctor[] | ServiceItem[]
  campaign: {
    name: string
    source: string
  }
} & SpotlightSessionCommonType

export type SpotlightClinicLocatorSessionApi = {
  specialties: string[]
  specialties_hashtag?: string
} & SpotlightSessionCommonTypeApi

export type SpotlightClinicLocatorSession = {
  specialties: string[]
  specialties_hashtag?: string
} & SpotlightSessionCommonType

export type SpotlightVideoItemApi = {
  url_video: string
  video_title: string
}
export type SpotlightVideoItem = {
  urlVideo: string
  videoTitle: string
}
export type SpotlightVideoSessionApi = {
  feature_video: SpotlightVideoItemApi
  video_list: SpotlightVideoItemApi[]
  full_width: boolean
  auto_play: boolean
  embed_short_reel?: boolean
} & SpotlightSessionCommonTypeApi

export type SpotlightVideoSectionSessionApi = {
  title: string
  video_url: string
} & SpotlightSessionCommonTypeApi

export type SpotlightVideoSession = {
  videoList: SpotlightVideoItem[]
  fullWidth: boolean
  autoPlay: boolean
  isShortReel?: boolean
} & SpotlightSessionCommonType

export type ArticleDisplayType =
  | 'grid'
  | 'list-contain-ads'
  | 'advance'
  | 'advance-with-loadmore'
  | 'key-pillars'

export type KeyPillar = {
  name: string
  slug: string
}

export type KeyPillarsArticlesTypeApi = {
  articles: ArticleApi[]
  key_pillars: KeyPillar
}

export type KeyPillarsArticlesType = {
  articles: Article[]
  keyPillars: {
    name: string
    slug: string
  }
}

export type SpotlightArticleSessionApi = {
  type_display: ArticleDisplayType
  list_articles: ArticleApi[] | KeyPillarsArticlesTypeApi[]
  read_more_articles: string[]
  list_categories: CategoryApi[]
} & SpotlightSessionCommonTypeApi

export type SpotlightArticleSession = {
  typeDisplay: ArticleDisplayType
  listArticles: Article[]
  listingByKeyPillar?: {
    listing: KeyPillarsArticlesType[]
    keyPillars?: KeyPillar[]
  }
  readMoreArticles: string[]
  listCategories: Category[]
} & SpotlightSessionCommonType

export type KeyPillarsClickableBannerTypeApi = {
  banners: ListBanner[]
  key_pillars: {
    name: string
    slug: string
  }
}

export type KeyPillarsClickableBannerType = {
  banners: ListBanner[]
  keyPillars?: {
    name: string
    slug: string
  }
}

export type SpotlightClickableBannerSessionApi = {
  list_banners: ListBanner[] | KeyPillarsClickableBannerTypeApi[]
  background_color: string
  apply_carousel: boolean
  require_login: boolean
  avata_icon: string
  token: string
  single_banner: SpotlightSingleClickableBannerTypeApi
  is_single_banner: boolean
  disclaimer: {
    title: string
    description: string
  }
  layout_key_pillars?: boolean
  leadgencode?: string
  expand_all_categories: boolean
} & SpotlightSessionCommonTypeApi

export type SpotlightSingleClickableBannerTypeApi = {
  icon: string
  banner: string
  url: string
  text_of_url: string
}

export type SpotlightSingleClickableBannerType = {
  icon: string
  banner: string
  url: string
  ctaTitle: string
}

export type SpotlightClickableSingleBannerSession = {
  requireLogin: boolean
  singleBanner: SpotlightSingleClickableBannerType
  isSingleBanner: boolean
  disclaimer: {
    title: string
    description: string
  }
} & SpotlightSessionCommonType

export type SpotlightEcommerceProductsSession = {
  productSkuList: string
  products: Product[]
} & SpotlightSessionCommonType

export type SpotlightEcommerceProductsSessionApi = {
  product_sku: string
  pcm_widget_title: string
} & SpotlightSessionCommonTypeApi

export enum SpotlightCommunityWithPostEmbedType {
  COMMUNITY = 'community',
  POST = 'post',
  MINIGAME = 'mini-game',
}

export type SpotlightCommunityWithPostsSession = {
  typeEmbed: SpotlightCommunityWithPostEmbedType
  communityId: string
  postsIds: string[]
  posts: TogetherTypes.Post[]
  community?: TogetherTypes.Community
  desktopBanner: string
  mobileBanner: string
  post: number
  postDetail?: TogetherTypes.Post
} & SpotlightSessionCommonType

export type SpotlightCommunityWithPostsSessionApi = {
  type_embeb: SpotlightCommunityWithPostEmbedType
  community_id: string
  posts: string[]
  desktop_banner: string
  mobile_banner: string
  post: number
} & SpotlightSessionCommonTypeApi

export type SpotlightClickableBannerSession = {
  backgroundColor: string
  applyCarousel: boolean
  requireLogin: boolean
  avatarIcon: string
  token: string
  isSingleBanner: boolean
  listing: KeyPillarsClickableBannerType[]
  leadgencode: any
  expandAllCategories: boolean
} & SpotlightSessionCommonType

export type ExpertCardApi = {
  card_color: string
  banner: string
  title: string
}

export type ExpertCard = {
  cardColor: string
  banner: string
  title: string
}

export type SpotlightChatWithExpertSessionApi = SpotlightClickableBannerSessionApi

export type SpotlightChatWithExpertSession = SpotlightClickableBannerSession

export type ServiceCategoryApi = {
  id: number | string
}
export type ServiceItemApi = {
  categories: ServiceCategoryApi
  services: CareServiceInfoApi[]
}
export type ServiceItem = {
  categoryId: number | string
  services: CareService[]
}
export type TopServicesApi = {
  service_list: ServiceItemApi[]
}
export type TopServices = {
  serviceList: ServiceItem[]
}
export type CityType = {
  id: number
  name: string
  slug: string
}
export type TopServicesByCitiesApi = {
  city: CityType
  service_list: ServiceItemApi[]
}
export type TopServicesByCities = {
  city: CityType
  serviceList: ServiceItem[]
}

export type ContentBanner = {
  icon: string
  title: string
  description: string
  url: string
  banner: string
}

export type ContentBannerSessionApi = {
  single_layout: boolean
  list_banner: ContentBanner[]
} & SpotlightSessionCommonTypeApi

export type ContentBannerSession = {
  singleLayout: boolean
  listBanner: ContentBanner[]
} & SpotlightSessionCommonType

export type SpotlightFooterBannerSessionApi = {
  url: string
  banner_desktop: string
  banner_mobile: string
} & SpotlightSessionCommonTypeApi

export type SpotlightHealthToolListSessionApi = {
  health_tools: {
    title: string
    list_health_tools: ContentBanner[]
  }[]
} & SpotlightSessionCommonTypeApi

export type SpotlightHealthToolListSession = {
  healthTools: {
    title: string
    listHealthTools: ContentBanner[]
  }[]
} & SpotlightSessionCommonType

export type SpotlightVisionSessionApi = {
  image: string
} & SpotlightSessionCommonTypeApi

export type SpotlightVisionSession = {
  image: string
} & SpotlightSessionCommonType

export type SpotlightQuotationsSessionApi = {
  icon: string
  quotations: {
    name: string
    message: string
  }[]
} & SpotlightSessionCommonTypeApi

export type SpotlightQuotationsSession = {
  icon: string
  quotations: {
    name: string
    message: string
  }[]
} & SpotlightSessionCommonType

export type ScheduleEventCardApi = {
  event_time: {
    start_date: string
    end_date: string
    countdown_start: string
  }
  cta: {
    cta: string
    url: string
  }
  banner_event: {
    desktop: string
    mobile: string
  }
  leadgen_campaign: {
    visible_section: boolean
    campaign_code: string
    desktop: string
    mobile: string
    cta_text: string
  }
}

export type ScheduleEventCard = {
  id: string
  startDate: string
  endDate: string
  countdownStart: string
  ctaTitle: string
  ctaUrl: string
  bannersDesktop: string
  bannersMobile: string
  title?: string
  episode?: number
  state?: ScheduleEventStatus
  monthKey?: string
  leadgenCampaign: {
    code: string
    bannerDesktop: string
    bannerMobile: string
    ctaTitle: string
    enableLeadgenInline: boolean
  }
}

export type SpotlightScheduleEventSessionApi = {
  list_event: ScheduleEventCardApi[]
  mascott: string
} & SpotlightSessionCommonTypeApi

export type SpotlightScheduleEventSession = {
  listEvent: ScheduleEventCard[]
  maskcot: string
} & SpotlightSessionCommonType

export type ContentExpertCardApi = {
  display_name: string
  profile_url: string
  avatar_thumbnail: string
  role: string
  specialty: string
  organisation: string
  position: string
}

export type ContentExpertCard = {
  name: string
  url: string
  avatar: string
  role: string
  specialty: string
  organisation: string
  position: string
}

export type SpotlightContentExpertSessionApi = {
  doctors: ContentExpertCardApi[]
} & SpotlightSessionCommonTypeApi

export type SpotlightContentExpertSession = {
  doctors: ContentExpertCard[]
} & SpotlightSessionCommonType

export type CustomizeBlockItemApi = {
  icon_and_background: {
    icon: string
    background_color: string
  }
  header: {
    value: string
    color: string
  }
  content: {
    value: string
    color: string
  }
  description: {
    value: string
    color: string
  }
}

export type CustomizeBlockItem = {
  icon: string
  backgroundColor: string
  header: {
    value: string
    color: string
  }
  content: {
    value: string
    color: string
  }
  description: {
    value: string
    color: string
  }
}

export type SpotlightCustomizeBlockSessionApi = {
  list_customize_block: CustomizeBlockItemApi[]
} & SpotlightSessionCommonTypeApi

export type SpotlightCustomizeBlockSession = {
  listing: CustomizeBlockItem[]
} & SpotlightSessionCommonType
export type BannerItemTypeApi = {
  desktop: string
  mobile: string
  banner_setting: {
    title: string
    description: string
    button_text: string
    button_link: string
  }
}
export type KeyPillarBannerTypeApi = {
  key_pillars: {
    name: string
    slug: string
  }
  banners: BannerItemTypeApi[]
}

export type NonKeyPillarBannerTypeApi = BannerItemTypeApi

export type CtaBannerApi =
  | {
      cta_banner_type: 'single-image'
      layout:
        | 'image_overlay_content'
        | '2_column_layout_1_1'
        | '2_column_layout_2_1'
      desktop_banner: string
      mobile_banner: string
      content_setting: {
        position: 'left_banner' | 'right_banner'
        header: {
          text_color: string
          header_content: string
        }
        text: {
          text_color: string
          text_content: string
        }
        buttons: {
          button_color: string
          gradient_button: string
          open_new_tab: boolean
          primary: {
            button_name: string
            button_link: string
          }
          secondary_button: {
            button_name: string
            button_link: string
          }
        }
      }
    }
  | {
      cta_banner_type: 'multiple-image'
      layout: 'image_overlay_button' | 'image_on_top_button_below'
      open_new_tab: boolean
      fix_size_banner: boolean
      apply_carousel: boolean
      expand_all_categories: boolean
      layout_key_pillars: boolean
      color_list_banner: {
        title_color: string
        description_color: string
        button_color: string
        gradient_button: string
      }
      list_banners: (KeyPillarBannerTypeApi | NonKeyPillarBannerTypeApi)[]
    }

export type CtaBannerItem = {
  desktop: string
  mobile: string
  titleColor: string
  title: string
  descriptionColor: string
  description: string
  buttons: {
    primary?: {
      backgroundGradient: string
      backgroundColor: string
      openNewTab: boolean
      title: string
      url: string
    }
    secondary?: {
      backgroundGradient: string
      backgroundColor: string
      openNewTab: boolean
      title: string
      url: string
    }
  }
}

export type CtaGroupBannerItem = {
  keyPillars?: {
    name: string
    slug: string
  }
  banners: CtaBannerItem[]
}

export type CtaBanner =
  | {
      type: 'single-image'
      layout:
        | 'image_overlay_content'
        | '2_column_layout_1_1'
        | '2_column_layout_2_1'
      listing: CtaGroupBannerItem[]
      layoutPosition: 'left_banner' | 'right_banner'
    }
  | {
      type: 'multiple-image'
      layout: 'image_overlay_button' | 'image_on_top_button_below'
      listing: CtaGroupBannerItem[]
      fixSizeBanner: boolean
      applyCarousel: boolean
      layoutKeyPillars: boolean
      expandAllCategories: boolean
    }

export type SpotlightCTABannerSessionApi = CtaBannerApi &
  SpotlightSessionCommonTypeApi

export type SpotlightCTABannerSession = CtaBanner & SpotlightSessionCommonType
